import { useState } from "react";
import { Link } from "wouter";
import { ArrowLeft, Copy, Check, Code2, Key, Webhook, Bot, Zap, BookOpen, Rocket, Terminal, ChevronRight, Users } from "lucide-react";
import { Button } from "@/components/ui/button";

const sections = [
  { id: "quickstart", label: "Quickstart" },
  { id: "auth", label: "Authentication" },
  { id: "rate-limits", label: "Rate Limits" },
  { id: "workspace", label: "Workspace" },
  { id: "leads", label: "Leads" },
  { id: "content", label: "Content" },
  { id: "campaigns", label: "Campaigns" },
  { id: "analytics", label: "Analytics" },
  { id: "agents", label: "Agents" },
  { id: "generate", label: "AI Generate" },
  { id: "mcp", label: "MCP Server" },
  { id: "mcp-tools", label: "MCP Tool Reference" },
  { id: "webhooks", label: "Webhooks" },
];

function CodeBlock({ code, lang = "bash" }: { code: string; lang?: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="relative group">
      <pre className="bg-zinc-900 text-zinc-100 rounded-xl p-4 text-[13px] font-mono overflow-x-auto border border-zinc-800 leading-relaxed" data-testid="code-block">
        {code}
      </pre>
      <button
        className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity bg-zinc-700 hover:bg-zinc-600 rounded-lg p-1.5"
        onClick={() => { navigator.clipboard.writeText(code); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
        data-testid="button-copy-code"
      >
        {copied ? <Check size={14} className="text-green-400" /> : <Copy size={14} className="text-zinc-300" />}
      </button>
    </div>
  );
}

function Endpoint({ method, path, description, body, response, params }: { method: string; path: string; description: string; body?: string; response?: string; params?: { name: string; type: string; required?: boolean; desc: string }[] }) {
  const methodColor: Record<string, string> = {
    GET: "bg-green-500/20 text-green-400 border-green-500/30",
    POST: "bg-blue-500/20 text-blue-400 border-blue-500/30",
    PATCH: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
    DELETE: "bg-red-500/20 text-red-400 border-red-500/30",
  };
  return (
    <div className="border border-border rounded-xl overflow-hidden" data-testid={`endpoint-${method.toLowerCase()}-${path.replace(/[/:]/g, "-")}`}>
      <div className="flex items-center gap-3 px-4 py-3 bg-muted/30 border-b border-border">
        <span className={`px-2 py-0.5 rounded-md text-[11px] font-bold uppercase tracking-wider border ${methodColor[method] || "bg-muted"}`}>{method}</span>
        <code className="text-sm font-mono text-foreground/80">{path}</code>
      </div>
      <div className="p-4 space-y-3">
        <p className="text-sm text-muted-foreground">{description}</p>
        {params && params.length > 0 && (
          <div>
            <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2">Parameters</p>
            <div className="space-y-1.5">
              {params.map(p => (
                <div key={p.name} className="flex items-start gap-2 text-xs">
                  <code className="text-orange-400 shrink-0">{p.name}</code>
                  <span className="text-muted-foreground/60 shrink-0">{p.type}{p.required ? " · required" : " · optional"}</span>
                  <span className="text-muted-foreground">{p.desc}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        {body && (
          <div>
            <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2">Request Body</p>
            <CodeBlock code={body} lang="json" />
          </div>
        )}
        {response && (
          <div>
            <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2">Response</p>
            <CodeBlock code={response} lang="json" />
          </div>
        )}
      </div>
    </div>
  );
}

function McpTool({ name, description, params }: { name: string; description: string; params: { name: string; type: string; required?: boolean; desc: string }[] }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-border rounded-xl overflow-hidden" data-testid={`mcp-tool-card-${name}`}>
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between px-4 py-3 bg-muted/20 hover:bg-muted/30 transition-colors text-left"
      >
        <div className="flex items-center gap-3">
          <code className="text-sm font-mono text-green-400">{name}</code>
        </div>
        <ChevronRight size={14} className={`text-muted-foreground transition-transform ${open ? "rotate-90" : ""}`} />
      </button>
      {open && (
        <div className="p-4 space-y-3 border-t border-border bg-background/30">
          <p className="text-sm text-muted-foreground">{description}</p>
          {params.length > 0 && (
            <div>
              <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2">Parameters</p>
              <div className="space-y-1.5">
                {params.map(p => (
                  <div key={p.name} className="flex items-start gap-2 text-xs">
                    <code className="text-orange-400 shrink-0 min-w-[120px]">{p.name}</code>
                    <span className="text-muted-foreground/60 shrink-0 min-w-[80px]">{p.type}{p.required ? " · required" : " · optional"}</span>
                    <span className="text-muted-foreground">{p.desc}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const MCP_TOOLS = [
  {
    name: "get_workspace",
    description: "Get workspace info including brand name, industry, website URL, and active social platforms.",
    params: [],
  },
  {
    name: "get_strategy",
    description: "Get the current active marketing strategy and plan for the workspace.",
    params: [],
  },
  {
    name: "get_analytics",
    description: "Get aggregate content and lead performance metrics across posts, leads, and campaigns.",
    params: [],
  },
  {
    name: "post_to_social",
    description: "Schedule or publish a social media post using the workspace's connected OAuth accounts.",
    params: [
      { name: "platform", type: "string", required: true, desc: "Target platform: linkedin | x | instagram | facebook" },
      { name: "content", type: "string", required: true, desc: "Post text content" },
      { name: "scheduledAt", type: "string", required: false, desc: "ISO 8601 datetime to schedule. Omit to post immediately." },
    ],
  },
  {
    name: "get_content",
    description: "List content items (posts, drafts, scheduled) in the workspace.",
    params: [
      { name: "limit", type: "number", required: false, desc: "Max items to return (default 20)" },
      { name: "status", type: "string", required: false, desc: "Filter by status: draft | scheduled | published" },
    ],
  },
  {
    name: "generate_content",
    description: "Generate marketing copy for any platform using Gemini AI. Consumes workspace AI credits.",
    params: [
      { name: "prompt", type: "string", required: true, desc: "What to write about" },
      { name: "platform", type: "string", required: false, desc: "linkedin | x | instagram | email | blog" },
      { name: "tone", type: "string", required: false, desc: "professional | casual | bold | educational" },
    ],
  },
  {
    name: "get_campaigns",
    description: "List all ad campaigns for the workspace.",
    params: [
      { name: "limit", type: "number", required: false, desc: "Max campaigns to return (default 20)" },
    ],
  },
  {
    name: "create_lead",
    description: "Create a new lead in the CRM / Lead Desk.",
    params: [
      { name: "name", type: "string", required: true, desc: "Lead full name" },
      { name: "email", type: "string", required: false, desc: "Lead email address" },
      { name: "company", type: "string", required: false, desc: "Company or organisation" },
      { name: "source", type: "string", required: false, desc: "How the lead was acquired" },
      { name: "notes", type: "string", required: false, desc: "Initial notes about this lead" },
    ],
  },
  {
    name: "add_lead",
    description: "Add a lead to the CRM (alias for create_lead — same behaviour).",
    params: [
      { name: "name", type: "string", required: true, desc: "Lead full name" },
      { name: "email", type: "string", required: false, desc: "Lead email address" },
      { name: "company", type: "string", required: false, desc: "Company or organisation" },
      { name: "source", type: "string", required: false, desc: "How the lead was acquired" },
      { name: "notes", type: "string", required: false, desc: "Initial notes" },
    ],
  },
  {
    name: "get_leads",
    description: "Fetch leads with optional filters for nurture workflows.",
    params: [
      { name: "limit", type: "number", required: false, desc: "Max leads to return (default 20)" },
      { name: "due_soon", type: "boolean", required: false, desc: "Only return leads overdue for nurture (nextNurtureAt in the past)" },
      { name: "include_unscheduled", type: "boolean", required: false, desc: "Also include leads never nurtured (no nextNurtureAt). Pair with due_soon." },
      { name: "for_nurture", type: "boolean", required: false, desc: "Exclude LOST, CONVERTED, and OPTED_OUT leads" },
    ],
  },
  {
    name: "run_draper",
    description: "Trigger the Draper agent (Marketing Strategist) to generate or refresh your marketing plan.",
    params: [],
  },
  {
    name: "run_peggy",
    description: "Trigger the Peggy agent (Content Creator) to fill your content calendar.",
    params: [
      { name: "action", type: "string", required: false, desc: "fill_calendar | generate_posts (default: fill_calendar)" },
    ],
  },
  {
    name: "run_rogers",
    description: "Trigger the Rogers agent (Lead Nurturing) to engage and follow up with leads.",
    params: [],
  },
  {
    name: "rogers_score_and_qualify",
    description: "Score and qualify a lead using Rogers AI. Returns a readiness score (0–100), stage, and recommended next action. Pass a CRM lead_id OR inline lead context.",
    params: [
      { name: "lead_id", type: "number", required: false, desc: "CRM lead ID (omit if providing inline context)" },
      { name: "lead_name", type: "string", required: false, desc: "Lead full name (required if no lead_id)" },
      { name: "lead_email", type: "string", required: false, desc: "Lead email (required if no lead_id)" },
      { name: "lead_source", type: "string", required: false, desc: "How the lead was acquired" },
      { name: "lead_notes", type: "string", required: false, desc: "Known context about the lead" },
    ],
  },
  {
    name: "rogers_generate_sequence",
    description: "Generate a personalised 5-step closing sequence (Day 0 / 3 / 7 / 14 / 21 arc) for a lead.",
    params: [
      { name: "lead_id", type: "number", required: false, desc: "CRM lead ID (omit if providing inline context)" },
      { name: "lead_name", type: "string", required: false, desc: "Lead full name (required if no lead_id)" },
      { name: "lead_email", type: "string", required: false, desc: "Lead email (required if no lead_id)" },
      { name: "lead_source", type: "string", required: false, desc: "How the lead was acquired" },
    ],
  },
  {
    name: "rogers_research_prospect",
    description: "Research a prospect and return a structured intelligence brief: role, company, industry pain points, recent news, competitor context, and recommended outreach angle.",
    params: [
      { name: "lead_id", type: "number", required: false, desc: "CRM lead ID (optional)" },
      { name: "name", type: "string", required: false, desc: "Prospect full name" },
      { name: "email", type: "string", required: false, desc: "Prospect email (used to infer company domain)" },
      { name: "company", type: "string", required: false, desc: "Company or organisation name" },
      { name: "query", type: "string", required: false, desc: "Additional search context or specific question" },
      { name: "max_results", type: "number", required: false, desc: "Max web results (default 5)" },
    ],
  },
  {
    name: "rogers_handle_objection",
    description: "Generate an AI-powered objection-handling response for a specific objection type.",
    params: [
      { name: "objection_type", type: "string", required: true, desc: "price | timing | competitor | need_more_info | wrong_person" },
      { name: "lead_id", type: "number", required: false, desc: "CRM lead ID for personalisation (optional)" },
      { name: "business_name", type: "string", required: false, desc: "Business name (used if no lead_id)" },
      { name: "industry", type: "string", required: false, desc: "Industry for context" },
      { name: "lead_name", type: "string", required: false, desc: "Lead name for personalisation" },
    ],
  },
  {
    name: "rogers_generate_first_email",
    description: "Generate a world-class first outreach email, validated for placeholders and business-name presence.",
    params: [
      { name: "lead_id", type: "number", required: false, desc: "CRM lead ID (omit if providing inline context)" },
      { name: "lead_name", type: "string", required: false, desc: "Lead full name (required if no lead_id)" },
      { name: "lead_email", type: "string", required: false, desc: "Lead email (required if no lead_id)" },
      { name: "lead_source", type: "string", required: false, desc: "How the lead was acquired (optional)" },
    ],
  },
];

export default function ApiDocs() {
  const [activeSection, setActiveSection] = useState("quickstart");
  const [quickstartTab, setQuickstartTab] = useState<"claude" | "n8n" | "python">("claude");
  const origin = typeof window !== "undefined" ? window.location.origin : "https://your-app.replit.app";

  const scrollTo = (id: string) => {
    setActiveSection(id);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="page-api-docs">
      <div className="border-b border-border bg-card/50 sticky top-0 z-10 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link href="/settings">
              <Button variant="ghost" size="icon" data-testid="button-back-settings">
                <ArrowLeft size={18} />
              </Button>
            </Link>
            <div className="flex items-center gap-2">
              <BookOpen size={20} className="text-orange-400" />
              <h1 className="text-lg font-display font-bold">Developer Docs</h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a href="/settings" className="text-xs text-orange-400 hover:underline flex items-center gap-1" data-testid="link-get-api-key">
              <Key size={12} /> Get API Key
            </a>
            <span className="text-xs font-mono text-muted-foreground bg-muted/50 border border-border rounded-lg px-3 py-1">v1</span>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8 flex gap-8">
        <nav className="hidden lg:block w-52 shrink-0 sticky top-24 self-start space-y-0.5" data-testid="nav-api-docs">
          {sections.map(s => (
            <button
              key={s.id}
              onClick={() => scrollTo(s.id)}
              className={`block w-full text-left px-3 py-1.5 rounded-lg text-sm transition-colors ${activeSection === s.id ? "bg-orange-500/10 text-orange-400 font-medium" : "text-muted-foreground hover:text-foreground"}`}
              data-testid={`nav-${s.id}`}
            >
              {s.label}
            </button>
          ))}
        </nav>

        <div className="flex-1 min-w-0 space-y-14">

          {/* QUICKSTART */}
          <section id="quickstart" className="space-y-6">
            <div className="flex items-center gap-2">
              <Rocket size={22} className="text-orange-400" />
              <h2 className="text-2xl font-display font-bold">Quickstart</h2>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed max-w-2xl">
              drpr.ai is marketing infrastructure for external agents. Connect your Claude Desktop, n8n workflow, or custom Python script to your workspace in under 5 minutes.
            </p>

            {/* Steps */}
            <div className="space-y-4">
              <div className="flex items-start gap-4 p-4 bg-card/50 border border-border rounded-xl">
                <div className="w-7 h-7 rounded-full bg-orange-500/20 text-orange-400 text-sm font-bold flex items-center justify-center shrink-0">1</div>
                <div className="space-y-1">
                  <p className="font-semibold text-sm">Generate your API key</p>
                  <p className="text-xs text-muted-foreground">Go to <strong>Settings → API Access</strong> and click <em>Generate API Key</em>. Keys are prefixed <code className="text-orange-400">drpr_</code> and workspace-scoped.</p>
                </div>
              </div>
              <div className="flex items-start gap-4 p-4 bg-card/50 border border-border rounded-xl">
                <div className="w-7 h-7 rounded-full bg-orange-500/20 text-orange-400 text-sm font-bold flex items-center justify-center shrink-0">2</div>
                <div className="space-y-1">
                  <p className="font-semibold text-sm">Verify the connection</p>
                  <CodeBlock code={`curl "${origin}/api/v1/workspace" -H "X-API-Key: drpr_49_abc123..."`} />
                </div>
              </div>
              <div className="flex items-start gap-4 p-4 bg-card/50 border border-border rounded-xl">
                <div className="w-7 h-7 rounded-full bg-orange-500/20 text-orange-400 text-sm font-bold flex items-center justify-center shrink-0">3</div>
                <div className="space-y-2">
                  <p className="font-semibold text-sm">Connect your agent</p>

                  <div className="flex border-b border-border">
                    {(["claude", "n8n", "python"] as const).map(tab => (
                      <button
                        key={tab}
                        onClick={() => setQuickstartTab(tab)}
                        className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-[2px] ${quickstartTab === tab ? "border-orange-500 text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}
                        data-testid={`quickstart-tab-${tab}`}
                      >
                        {tab === "claude" ? "Claude Desktop" : tab === "n8n" ? "n8n" : "Python / Custom"}
                      </button>
                    ))}
                  </div>

                  {quickstartTab === "claude" && (
                    <div className="space-y-3 pt-2">
                      <p className="text-xs text-muted-foreground">
                        Edit <code className="bg-muted/50 px-1 rounded">~/Library/Application Support/Claude/claude_desktop_config.json</code> on macOS (or <code className="bg-muted/50 px-1 rounded">%APPDATA%\Claude\claude_desktop_config.json</code> on Windows):
                      </p>
                      <CodeBlock lang="json" code={`{
  "mcpServers": {
    "drpr": {
      "command": "npx",
      "args": ["@modelcontextprotocol/server-http"],
      "env": {
        "URL": "${origin}/mcp/v1",
        "X_API_KEY": "drpr_49_YOUR_KEY_HERE"
      }
    }
  }
}`} />
                      <p className="text-xs text-muted-foreground">Restart Claude Desktop. Your 18 drpr.ai tools will appear in the toolbar automatically.</p>
                    </div>
                  )}

                  {quickstartTab === "n8n" && (
                    <div className="space-y-3 pt-2">
                      <p className="text-xs text-muted-foreground">Use the <strong>HTTP Request</strong> node in any n8n workflow. Set auth as a custom header:</p>
                      <div className="text-xs space-y-2 text-muted-foreground bg-muted/30 rounded-xl p-3 border border-border">
                        <div><span className="font-semibold text-foreground">Method:</span> POST</div>
                        <div><span className="font-semibold text-foreground">URL:</span> {origin}/api/v1/leads</div>
                        <div><span className="font-semibold text-foreground">Header name:</span> X-API-Key</div>
                        <div><span className="font-semibold text-foreground">Header value:</span> drpr_49_YOUR_KEY_HERE</div>
                      </div>
                      <p className="text-xs text-muted-foreground">Or for MCP-style tool calls, POST to <code className="text-orange-400">{origin}/mcp/v1/tools/call</code> with <code className="text-orange-400">{`{"name":"add_lead","arguments":{...}}`}</code></p>
                    </div>
                  )}

                  {quickstartTab === "python" && (
                    <div className="space-y-3 pt-2">
                      <CodeBlock lang="python" code={`import requests

API_KEY = "drpr_49_YOUR_KEY_HERE"
BASE    = "${origin}"

headers = {"X-API-Key": API_KEY, "Content-Type": "application/json"}

# Get workspace info
ws = requests.get(f"{BASE}/api/v1/workspace", headers=headers).json()
print(ws["data"]["brandName"])

# Add a lead
lead = requests.post(f"{BASE}/api/v1/leads", headers=headers, json={
    "name": "Jane Smith",
    "email": "jane@example.com",
    "company": "Acme Corp",
    "source": "inbound_form",
}).json()
print(f"Created lead #{lead['data']['id']}")

# Run Rogers to nurture leads
run = requests.post(f"{BASE}/api/v1/agents/run", headers=headers, json={
    "agent": "rogers"
}).json()
print(f"Agent run started: {run['data']['runId']}")`} />
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="bg-orange-500/5 border border-orange-500/20 rounded-xl p-4 space-y-1">
              <div className="flex items-center gap-2">
                <Users size={14} className="text-orange-400" />
                <p className="text-sm font-semibold text-orange-400">Agency note</p>
              </div>
              <p className="text-xs text-muted-foreground">Each workspace has its own API key. If you manage multiple client workspaces, generate a separate key per workspace from each workspace's Settings page. All API calls are scoped to and logged against the issuing workspace.</p>
            </div>
          </section>

          {/* AUTH */}
          <section id="auth" className="space-y-4">
            <div className="flex items-center gap-2">
              <Key size={20} className="text-orange-400" />
              <h2 className="text-xl font-display font-bold">Authentication</h2>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">
              All API requests require an API key. Generate one from <strong>Settings → API Access</strong>.
              Pass the key via the <code className="text-orange-400">X-API-Key</code> header, the <code className="text-orange-400">Authorization: Bearer</code> header, or the <code className="text-orange-400">?key=</code> query param.
            </p>
            <CodeBlock code={`# Header (recommended)
curl "${origin}/api/v1/workspace" -H "X-API-Key: drpr_49_abc123..."

# Bearer token
curl "${origin}/api/v1/workspace" -H "Authorization: Bearer drpr_49_abc123..."

# Query param (avoid in production)
curl "${origin}/api/v1/workspace?key=drpr_49_abc123..."`} />
            <div className="bg-muted/30 border border-border rounded-xl p-4 text-xs text-muted-foreground space-y-1">
              <p><strong className="text-foreground">Key format:</strong> <code>drpr_&lt;workspaceId&gt;_&lt;hex&gt;</code> — keys are hashed server-side and never stored in plaintext.</p>
              <p><strong className="text-foreground">Key scope:</strong> A key is bound to exactly one workspace. All actions performed with a key are attributed to that workspace.</p>
              <p><strong className="text-foreground">Error on invalid key:</strong> <code>401 {"{ \"error\": \"Invalid or revoked API key\" }"}</code></p>
            </div>
          </section>

          {/* RATE LIMITS */}
          <section id="rate-limits" className="space-y-4">
            <div className="flex items-center gap-2">
              <Zap size={20} className="text-yellow-400" />
              <h2 className="text-xl font-display font-bold">Rate Limits</h2>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">
              The API allows <strong>100 requests per minute</strong> per workspace. Exceeding this returns <code className="text-red-400">429 Too Many Requests</code> with a <code>retryAfterMs</code> field indicating milliseconds to wait.
            </p>
            <CodeBlock lang="json" code={`// 429 response body
{
  "error": "Rate limit exceeded (100 req/min). Retry after reset.",
  "retryAfterMs": 23400
}`} />
          </section>

          {/* WORKSPACE */}
          <section id="workspace" className="space-y-4">
            <div className="flex items-center gap-2">
              <Code2 size={20} className="text-blue-400" />
              <h2 className="text-xl font-display font-bold">Workspace</h2>
            </div>
            <Endpoint method="GET" path="/api/v1/workspace" description="Get basic workspace info including brand name, industry, and URL." response={`{
  "success": true,
  "data": {
    "id": 49,
    "name": "My Workspace",
    "brandName": "Acme Corp",
    "industry": "SaaS",
    "url": "https://acme.com"
  }
}`} />
          </section>

          {/* LEADS */}
          <section id="leads" className="space-y-4">
            <h2 className="text-xl font-display font-bold">Leads</h2>
            <Endpoint method="GET" path="/api/v1/leads" description="List leads. Supports pagination with limit (max 200) and offset."
              params={[
                { name: "limit", type: "number", desc: "Max leads to return (default 50, max 200)" },
                { name: "offset", type: "number", desc: "Number of leads to skip for pagination" },
              ]}
              response={`{
  "success": true,
  "data": [
    { "id": 1, "name": "Jane Smith", "email": "jane@co.com", "status": "new", "engagementScore": 60 }
  ],
  "total": 42
}`} />
            <Endpoint method="POST" path="/api/v1/leads" description="Create a new lead. At least name or email is required. Fires a lead.created webhook event." body={`{
  "name": "Jane Smith",
  "email": "jane@company.com",
  "company": "Acme Corp",
  "source": "website",
  "phone": "+1234567890",
  "notes": "Came in via demo request"
}`} response={`{ "success": true, "data": { "id": 123, "name": "Jane Smith", "status": "new", ... } }`} />
            <Endpoint method="PATCH" path="/api/v1/leads/:id" description="Update a lead's fields. Fires a lead.nurtured webhook event when status or score changes."
              params={[
                { name: "id", type: "number", required: true, desc: "Lead ID (URL param)" },
              ]}
              body={`{
  "status": "qualified",
  "score": 85,
  "company": "Acme Corp",
  "notes": "Attended demo, very interested"
}`} response={`{ "success": true, "data": { "id": 123, "status": "qualified", "engagementScore": 85, ... } }`} />
          </section>

          {/* CONTENT */}
          <section id="content" className="space-y-4">
            <h2 className="text-xl font-display font-bold">Content</h2>
            <Endpoint method="GET" path="/api/v1/content" description="List content items (posts, drafts, scheduled)."
              params={[
                { name: "limit", type: "number", desc: "Max items (default 50, max 200)" },
                { name: "offset", type: "number", desc: "Pagination offset" },
              ]}
              response={`{
  "success": true,
  "data": [{ "id": 1, "content": "...", "platform": "linkedin", "status": "draft" }],
  "total": 15
}`} />
            <Endpoint method="POST" path="/api/v1/posts" description="Create a new social post or draft. Platform defaults to linkedin." body={`{
  "platform": "linkedin",
  "content": "Exciting news from Acme Corp!",
  "status": "draft",
  "scheduledAt": "2025-06-01T10:00:00Z"
}`} response={`{ "success": true, "data": { "id": 55, "platform": "linkedin", "status": "draft", ... } }`} />
            <Endpoint method="PATCH" path="/api/v1/content/:id/schedule" description="Schedule a content item by setting its scheduledAt time. Status is set to scheduled automatically."
              params={[{ name: "id", type: "number", required: true, desc: "Content item ID (URL param)" }]}
              body={`{ "scheduledAt": "2025-06-01T10:00:00Z" }`}
              response={`{ "success": true, "data": { "id": 55, "status": "scheduled", "scheduledAt": "2025-06-01T10:00:00.000Z" } }`} />
          </section>

          {/* CAMPAIGNS */}
          <section id="campaigns" className="space-y-4">
            <h2 className="text-xl font-display font-bold">Campaigns</h2>
            <Endpoint method="GET" path="/api/v1/campaigns" description="List all ad campaigns for the workspace." response={`{
  "success": true,
  "data": [{ "id": 1, "name": "Spring Sale", "platform": "meta", "status": "draft", "budget": "500" }]
}`} />
            <Endpoint method="POST" path="/api/v1/campaigns" description="Create a new ad campaign. Fires a campaign.launched webhook event." body={`{
  "name": "Spring Sale",
  "platform": "meta",
  "objective": "conversions",
  "budget": "500",
  "targetAudience": { "age_min": 25, "age_max": 45 },
  "status": "draft"
}`} response={`{ "success": true, "data": { "id": 7, "name": "Spring Sale", "status": "draft", ... } }`} />
          </section>

          {/* ANALYTICS */}
          <section id="analytics" className="space-y-4">
            <h2 className="text-xl font-display font-bold">Analytics</h2>
            <Endpoint method="GET" path="/api/v1/analytics" description="Get aggregate analytics across posts, leads, and campaigns." response={`{
  "success": true,
  "data": {
    "posts": { "total": 45, "published": 30, "scheduled": 5 },
    "leads": { "total": 120, "new": 15 },
    "campaigns": { "total": 3, "active": 1 }
  }
}`} />
          </section>

          {/* AGENTS */}
          <section id="agents" className="space-y-4">
            <div className="flex items-center gap-2">
              <Bot size={20} className="text-purple-400" />
              <h2 className="text-xl font-display font-bold">Agents</h2>
            </div>
            <p className="text-sm text-muted-foreground">Trigger AI agents asynchronously and poll for results. Agents available: <code className="text-orange-400">draper</code>, <code className="text-orange-400">peggy</code>, <code className="text-orange-400">pete</code>, <code className="text-orange-400">rogers</code>.</p>
            <Endpoint method="POST" path="/api/v1/agents/run" description="Trigger an AI agent run. Returns immediately with a run ID — the agent executes asynchronously." body={`{
  "agent": "draper",
  "action": "generate_strategy"
}`} response={`{
  "success": true,
  "data": { "runId": 42, "agent": "Draper", "status": "started" }
}`} />
            <Endpoint method="GET" path="/api/v1/agents/run/:runId" description="Get the status and result of an agent run by run ID."
              params={[{ name: "runId", type: "number", required: true, desc: "Run ID returned from POST /agents/run" }]}
              response={`{
  "success": true,
  "data": {
    "id": 42,
    "agent": "Draper",
    "status": "completed",
    "actionsCount": 5,
    "summary": "Strategy generated for Q2 2025",
    "totalCost": 0.0012
  }
}`} />
          </section>

          {/* AI GENERATE */}
          <section id="generate" className="space-y-4">
            <h2 className="text-xl font-display font-bold">AI Content Generation</h2>
            <Endpoint method="POST" path="/api/v1/content/generate" description="Generate marketing copy using Gemini AI. Consumes workspace AI credits. Returns generated text immediately (not async)." body={`{
  "prompt": "Announce our new product launch for enterprise customers",
  "platform": "linkedin",
  "tone": "professional"
}`} response={`{
  "success": true,
  "data": {
    "content": "We're thrilled to announce...",
    "platform": "linkedin",
    "tone": "professional"
  }
}`} />
          </section>

          {/* MCP SERVER */}
          <section id="mcp" className="space-y-4">
            <div className="flex items-center gap-2">
              <Terminal size={20} className="text-green-400" />
              <h2 className="text-xl font-display font-bold">MCP Server</h2>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">
              drpr.ai implements the <strong>Model Context Protocol (MCP)</strong>, letting AI agents like Claude Desktop call your workspace's marketing tools natively.
              The server base URL is <code className="text-orange-400">{origin}/mcp/v1</code>.
              Authenticate with your <code className="text-orange-400">X-API-Key</code> header on every request.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Endpoint method="GET" path="/mcp/v1" description="Get server manifest: name, version, description, and full tool list." />
              <Endpoint method="GET" path="/mcp/v1/tools" description="Get all 18 tool definitions with their full JSON Schema input schemas." />
              <Endpoint method="POST" path="/mcp/v1/tools/call" description="Execute an MCP tool by name with arguments." />
            </div>
            <Endpoint method="POST" path="/mcp/v1/tools/call" description="Standard MCP tool call. Pass the tool name and arguments." body={`{
  "name": "add_lead",
  "arguments": {
    "name": "Jane Smith",
    "email": "jane@acme.com",
    "company": "Acme Corp"
  }
}`} response={`{
  "content": [{ "type": "text", "text": "Lead added: Jane Smith (ID: 45)" }]
}`} />
            <p className="text-xs text-muted-foreground">You can also call tools via the shorthand route: <code className="text-orange-400">POST /mcp/v1/tools/:toolName</code> where the request body is the arguments directly.</p>
          </section>

          {/* MCP TOOL REFERENCE */}
          <section id="mcp-tools" className="space-y-4">
            <div className="flex items-center gap-2">
              <Bot size={20} className="text-green-400" />
              <h2 className="text-xl font-display font-bold">MCP Tool Reference</h2>
            </div>
            <p className="text-sm text-muted-foreground">All 18 tools available on the drpr.ai MCP server. Click a tool to expand its parameter list.</p>

            <div className="space-y-2">
              <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Workspace & Analytics</p>
              {MCP_TOOLS.filter(t => ["get_workspace", "get_strategy", "get_analytics"].includes(t.name)).map(t => (
                <McpTool key={t.name} {...t} />
              ))}
            </div>

            <div className="space-y-2">
              <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Content & Campaigns</p>
              {MCP_TOOLS.filter(t => ["post_to_social", "get_content", "generate_content", "get_campaigns"].includes(t.name)).map(t => (
                <McpTool key={t.name} {...t} />
              ))}
            </div>

            <div className="space-y-2">
              <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">CRM & Leads</p>
              {MCP_TOOLS.filter(t => ["create_lead", "add_lead", "get_leads"].includes(t.name)).map(t => (
                <McpTool key={t.name} {...t} />
              ))}
            </div>

            <div className="space-y-2">
              <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Agent Execution</p>
              {MCP_TOOLS.filter(t => ["run_draper", "run_peggy", "run_rogers"].includes(t.name)).map(t => (
                <McpTool key={t.name} {...t} />
              ))}
            </div>

            <div className="space-y-2">
              <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Rogers BizDev Tools</p>
              <p className="text-xs text-muted-foreground">Advanced sales intelligence tools powered by Rogers. Each accepts either a CRM <code className="text-orange-400">lead_id</code> (to pull from your Lead Desk) or inline lead context.</p>
              {MCP_TOOLS.filter(t => t.name.startsWith("rogers_")).map(t => (
                <McpTool key={t.name} {...t} />
              ))}
            </div>
          </section>

          {/* WEBHOOKS */}
          <section id="webhooks" className="space-y-4">
            <div className="flex items-center gap-2">
              <Webhook size={20} className="text-orange-400" />
              <h2 className="text-xl font-display font-bold">Webhooks</h2>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Register webhook endpoints to receive real-time notifications when events happen in your workspace.
              Payloads are signed with HMAC-SHA256 using the webhook secret, sent in the <code className="text-orange-400">X-Drpr-Signature</code> header.
              Failed deliveries retry up to 3 times with exponential backoff.
            </p>
            <p className="text-xs text-muted-foreground">Manage webhooks in <strong>Settings → Webhooks</strong> or via the workspace API.</p>

            <div className="bg-muted/30 border border-border rounded-xl p-4">
              <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">Available Events</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {[
                  { event: "lead.created", desc: "New lead added to Lead Desk" },
                  { event: "lead.nurtured", desc: "Lead status or score updated" },
                  { event: "lead.cold", desc: "Lead marked cold / unresponsive" },
                  { event: "lead.replied", desc: "Lead replied to outreach" },
                  { event: "strategy.approved", desc: "Marketing plan approved" },
                  { event: "strategy.complete", desc: "Strategy generation completed" },
                  { event: "content.approved", desc: "Content item approved for posting" },
                  { event: "content.rejected", desc: "Content item rejected in review" },
                  { event: "agent.completed", desc: "An agent run finished successfully" },
                  { event: "agent.failed", desc: "An agent run encountered an error" },
                  { event: "campaign.launched", desc: "New campaign created via API" },
                  { event: "campaign.underperforming", desc: "Pete detected underperformance" },
                ].map(({ event, desc }) => (
                  <div key={event} className="flex items-start gap-2" data-testid={`webhook-event-${event}`}>
                    <code className="text-xs font-mono text-orange-400 shrink-0">{event}</code>
                    <span className="text-xs text-muted-foreground">{desc}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-muted/30 border border-border rounded-xl p-4 space-y-3">
              <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Webhook Payload</p>
              <CodeBlock lang="json" code={`{
  "event": "lead.created",
  "data": {
    "leadId": 123,
    "leadName": "Jane Smith",
    "leadEmail": "jane@co.com",
    "source": "api_v1"
  },
  "timestamp": "2025-06-01T12:00:00.000Z",
  "webhookId": 1
}`} />
            </div>

            <div className="bg-muted/30 border border-border rounded-xl p-4 space-y-3">
              <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Verifying Signatures</p>
              <CodeBlock code={`const crypto = require("crypto");

function verifySignature(rawBody, secret, signature) {
  const expected = crypto
    .createHmac("sha256", secret)
    .update(rawBody)   // raw Buffer or string — do NOT JSON.parse first
    .digest("hex");
  return crypto.timingSafeEqual(
    Buffer.from(expected),
    Buffer.from(signature)
  );
}

// Express example
app.post("/webhook", express.raw({ type: "*/*" }), (req, res) => {
  const sig = req.headers["x-drpr-signature"];
  if (!verifySignature(req.body, process.env.WEBHOOK_SECRET, sig)) {
    return res.status(401).send("Invalid signature");
  }
  const payload = JSON.parse(req.body.toString());
  console.log("Event:", payload.event, payload.data);
  res.sendStatus(200);
});`} />
            </div>
          </section>

        </div>
      </div>
    </div>
  );
}
